/* eslint-disable @typescript-eslint/promise-function-async, @typescript-eslint/indent */
import {
  useMutation,
  UseMutationResult,
  useQueries,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query'
import { AxiosError } from 'axios'
import _, { bind } from 'lodash'

import {
  DatasourceMutationVariables,
  DatasourceRequest,
  DatasourceRequestParams,
  DatasourceResponse,
  Event,
  EventResult,
} from 'common/interfaces'
import { useHeapDispatch } from 'context/Heap'
import api from 'utils/api'
import { useTemplate } from 'utils/hook'

function processValues(
  object: DatasourceRequestParams,
  func: (string: string) => string
): DatasourceRequestParams {
  return Object.fromEntries(
    Object.entries(object).map(([key, val]) => {
      if (val === null) {
        return [key, val]
      }

      switch (typeof val) {
        case 'object':
          return [key, processValues(val as DatasourceRequestParams, func)]
        case 'string':
          return [key, func(val)]
        default:
          return [key, val]
      }
    })
  )
}

export async function getDatasource(requestVars: DatasourceRequest): Promise<DatasourceResponse> {
  const { datasource: dsId, ...body } = requestVars
  const response = await api.post<DatasourceResponse>(`datasources/${dsId}/results/`, body)

  return response.data
}

export type UseDatasourceQueryOptions = UseQueryOptions<DatasourceResponse, AxiosError, EventResult>
export type UseDatasourceQueryReturn = Array<UseQueryResult<EventResult, AxiosError>>
export type UseDatasourceMutationReturn = UseMutationResult<
  EventResult[],
  AxiosError,
  DatasourceMutationVariables
>

export function useDatasourceQuery(
  events: Event[],
  enabled?: boolean,
  withRefetchOnMount = false
): UseDatasourceQueryReturn {
  const render = useTemplate()
  const [dispatch] = useHeapDispatch()

  return useQueries(
    events.map((event) => {
      const requestVariables: DatasourceRequest = {
        datasource: event.datasource,
        transform: render(event.transform),
        params: processValues(event.parameters, render),
      }

      const options: UseDatasourceQueryOptions = {
        enabled,
        queryKey: ['event', event.id],
        queryFn: () => getDatasource(requestVariables),
        onSettled: (data) => {
          if (event.store === 'heap') {
            dispatch({ type: 'add', fields: data?.heap ?? {} })
          }
        },
        select: (data) => ({
          heap: event.store === 'heap' ? data.results : null,
          props: event.store === 'props' ? data.results : null,
        }),
        refetchOnMount: withRefetchOnMount,
      }

      return options
    })
  ) as UseDatasourceQueryReturn
}

export function useDatasourceMutation(events: Event[]): UseDatasourceMutationReturn {
  const render = useTemplate()
  const [dispatch] = useHeapDispatch()

  return useMutation<EventResult[], AxiosError, DatasourceMutationVariables>((params) => {
    return Promise.all(
      events.map(async (event) => {
        const requestVariables: DatasourceRequest = {
          datasource: event.datasource,
          transform: render(event.transform),
          params: processValues(event.parameters, bind(render, null, _, params)),
        }
        const data = await getDatasource(requestVariables)

        if (event.store === 'heap') {
          dispatch({ type: 'add', fields: data.results })
        }

        return {
          heap: event.store === 'heap' ? data.results : null,
          props: event.store === 'props' ? data.results : null,
        }
      })
    )
  })
}
